/**
 * Date utility functions for handling timestamps from the backend
 * 
 * The backend returns timestamps in two formats:
 * - "2025-10-23 22:44:31" (space-separated, no timezone - should be treated as UTC)
 * - "2025-10-23T23:45:35.000Z" (ISO format with Z - already UTC)
 */

import { formatDistanceToNow } from 'date-fns';

/**
 * Parse a timestamp from the backend as UTC
 * Handles both formats: "2025-10-23 22:44:31" and "2025-10-23T23:45:35.000Z"
 */
export function parseUTCDate(dateString: string | null | undefined): Date | null {
  if (!dateString) return null;

  try {
    // If the string already has a 'Z' or timezone offset, use it directly
    if (dateString.includes('Z') || dateString.match(/[+-]\d{2}:\d{2}$/)) {
      return new Date(dateString);
    }

    // Otherwise, it's likely in format "2025-10-23 22:44:31" which should be treated as UTC
    // Replace space with 'T' and add 'Z' to make it a proper ISO string
    const isoString = dateString.replace(' ', 'T') + 'Z';
    return new Date(isoString);
  } catch {
    return null;
  }
}

/**
 * Format a date string from the backend as "X ago" or "in X"
 * Automatically handles UTC parsing
 */
export function formatDateDistance(
  dateString: string | null | undefined,
  options?: { addSuffix?: boolean }
): string {
  const date = parseUTCDate(dateString);
  if (!date || isNaN(date.getTime())) {
    return 'Invalid date';
  }

  try {
    return formatDistanceToNow(date, { addSuffix: options?.addSuffix ?? true });
  } catch {
    return 'Invalid date';
  }
}

